import { Injectable } from '@angular/core';
import { ANONYMOUS_CONSENT_STATUS, BaseSiteService, ConsentService, CxEvent, WindowRef } from '@spartacus/core';
import { TmsCollector, TmsCollectorConfig, WindowObject } from '@spartacus/tracking/tms/core';
import { forkJoin, map, merge } from 'rxjs';
import { distinctUntilChanged, skip, take } from 'rxjs/operators';
import { EnvironmentService } from './environment.service';

interface GtmCollectorConfig extends TmsCollectorConfig {
  gtmId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GtmCollectorService implements TmsCollector {
  private consentTemplates = [
    'ANALYTICS',
    'MARKETING',
    'FUNCTIONAL',
    'ESSENTIAL',
  ];

  constructor(
    protected winRef: WindowRef,
    protected environmentService: EnvironmentService,
    private consentService: ConsentService,
    private baseSiteService: BaseSiteService
  ) {}

  init(config: GtmCollectorConfig, windowObject: WindowObject): void {
    const dataLayerProperty = config.dataLayerProperty ?? 'dataLayer';

    // Step 1: Initialize GTM script
    this.baseSiteService.getActive().subscribe((activeSiteId) => {
      if (!activeSiteId) return;

      const gtmId = this.environmentService.getCurrentEnvironment().tracking.google.tagManager.id[activeSiteId];
      const server =
        this.environmentService.getCurrentEnvironment().tracking.google.tagManager.server[activeSiteId] ||
        'https://www.googletagmanager.com';

      if (!gtmId) return;

      windowObject[dataLayerProperty] = windowObject[dataLayerProperty] ?? [];

      // Load GTM script if not already loaded
      this.loadGtmScript(windowObject, dataLayerProperty, gtmId, server);

      // Step 2: Initial push of consent states for all templates
      this.pushInitialConsentStates(windowObject, dataLayerProperty);

      // Step 3: Subscribe to consent changes
      this.subscribeToConsentChanges(windowObject, dataLayerProperty);
    });
  }

  private loadGtmScript(windowObject: WindowObject, dataLayerProperty: string, gtmId: string, server: string): void {
    const doc = this.winRef.document;

    if (doc.querySelector(`script[src="${server}/gtm.js?id=${gtmId}"]`)) {
      return; // Already loaded
    }

    windowObject[dataLayerProperty].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const firstScript = doc.getElementsByTagName('script')[0];
    const gtmScript = doc.createElement('script') as HTMLScriptElement;
    const dataLayerParam = dataLayerProperty !== 'dataLayer' ? '&l=' + dataLayerProperty : '';

    gtmScript.async = true;
    gtmScript.src = `${server}/gtm.js?id=${gtmId}${dataLayerParam}`;
    firstScript.parentNode?.insertBefore(gtmScript, firstScript);
  }

  private pushInitialConsentStates(windowObject: WindowObject, dataLayerProperty: string): void {
    forkJoin(
      this.consentTemplates.map((template) =>
        this.consentService.checkConsentGivenByTemplateId(template).pipe(
          take(1),
          map((isGiven) => ({
            template,
            consentState: isGiven ? ANONYMOUS_CONSENT_STATUS.GIVEN : ANONYMOUS_CONSENT_STATUS.WITHDRAWN,
          }))
        )
      )
    ).subscribe((consents) => {
      consents.forEach((consent) => {
        windowObject[dataLayerProperty].push({
          event: 'consents_update',
          consent_id: consent.template,
          consent_state: consent.consentState,
        });
      });
    });
  }

  private subscribeToConsentChanges(windowObject: WindowObject, dataLayerProperty: string): void {
    merge(
      ...this.consentTemplates.map((template) =>
        this.consentService.checkConsentGivenByTemplateId(template).pipe(
          skip(1), // Skip initial value (already pushed in step 2)
          distinctUntilChanged(),
          map((isGiven) => ({
            id: template,
            consentState: isGiven ? ANONYMOUS_CONSENT_STATUS.GIVEN : ANONYMOUS_CONSENT_STATUS.WITHDRAWN,
          }))
        )
      )
    ).subscribe((consent) => {
      windowObject[dataLayerProperty].push({
        event: 'consents_update',
        consent_id: consent.id,
        consent_state: consent.consentState,
      });
    });
  }

  pushEvent<_ extends CxEvent>(config: GtmCollectorConfig, windowObject: WindowObject, event: any): void {
    const dataLayerProperty = config.dataLayerProperty ?? 'dataLayer';

    if (windowObject[dataLayerProperty]) {
      windowObject[dataLayerProperty].push(event);
    }
  }
}
